import React from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';

import { Icon, Translate, SafeAnchor, RatingIcons } from '@hydra/common/components';

import { useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3)
  },
  iconContainer: {
    display: 'flex',
    marginBottom: theme.spacing(3)
  },
  addSocialLink: {
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    marginBottom: theme.spacing(3)
  },
  localDirectoryIcon: {
    flexBasis: '10%'
  },
  localDirectoryBusinessInfo: {
    flexBasis: '50%',
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    }
  },
  localDirectoryReviewsSummary: {
    flexBasis: '30%'
  },
  iconReviews: {
    marginRight: theme.spacing(1)
  },
  reviewsWrapper: {
    display: 'flex',
    alignItems: 'start'
  }
}));

const directoryInfo = {
  Yelp: {
    displayName: 'local_yelp',
    icon: 'yelp'
  },
  GoogleMyBusiness: {
    displayName: 'local_google_my_business',
    icon: 'google'
  }
};

export const CriteriumLocalDirectory = ({ data, directory, canChangeAccount }) => {
  const classes = useStyles();
  const { translate } = useI18n();

  if (!data) {
    return canChangeAccount
      ? (
        <Translate
          className={classes.addSocialLink}
          component='div'
          content={`add_${directory.toLowerCase()}_or_create_title`}
        />
        )
      : (
        <div className={classes.iconContainer}>
          <Icon
            className={classes.localDirectoryIcon}
            type={directoryInfo[directory].icon}
            size='lg'
          />
          <Translate
            content='local_not_found'
            with={{ directoryName: translate(directoryInfo[directory].displayName) }}
          />
        </div>
        );
  }

  const { name, isClaimed, address, phone, reviewsCount, pageLink, rating, city, country } = data;
  const claimedText = <Translate content={isClaimed ? 'local_already_claimed' : 'local_claim_listing'} />;

  const hasReviews = (reviewsCount || 0) > 0;
  const hasRatings = (typeof rating === 'number');

  const getAddressSection = () => {
    const addressLine = [address, city]
      .filter(part => part)
      .join(', ');

    if (!addressLine && !country) {
      return null;
    }

    const lineBreak = (addressLine && country) ? <br /> : null;

    return (
      <p>
        {addressLine}
        {lineBreak}
        {country}
      </p>
    );
  };

  const reviewsCountText = (
    <>
      {`${reviewsCount} `}
      <Translate key='reviews-count-translation' content='local_total_reviews' />
    </>
  );

  return (
    <div className={classes.root}>
      <Icon
        className={classes.localDirectoryIcon}
        type={directoryInfo[directory].icon}
        size='lg'
      />
      <div className={classes.localDirectoryBusinessInfo}>
        <p><strong>{name}</strong></p>
        <p>
          {canChangeAccount && <Translate content='change_account' />}
          {
            !isClaimed &&
              <>
                {canChangeAccount && ' - '}
                <SafeAnchor href={pageLink} rel='nofollow noopener noreferrer' target='_blank'>{claimedText}</SafeAnchor>
              </>
          }
        </p>
        {getAddressSection()}
        {phone && <p>{phone}</p>}
      </div>
      <div className={classes.localDirectoryReviewsSummary}>
        {
          hasRatings && <RatingIcons size='sm' max={5} type='rating' rating={rating} />
        }
        <div className={classes.reviewsWrapper}>
          <Icon className={classes.iconReviews} type='conversation' />
          {
            hasReviews
              ? <SafeAnchor href='#criterium-local_online_reviews' rel='nofollow noopener noreferrer' target='_blank'>{reviewsCountText}</SafeAnchor>
              : reviewsCountText
          }
        </div>
      </div>
    </div>
  );
};

CriteriumLocalDirectory.propTypes = {
  directory: PropTypes.string,
  data: PropTypes.shape({
    isClaimed: PropTypes.bool,
    name: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    phone: PropTypes.string,
    pageLink: PropTypes.string,
    reviewsCount: PropTypes.number,
    rating: PropTypes.number
  }),
  canChangeAccount: PropTypes.bool
};

export default CriteriumLocalDirectory;
